import { ApiError } from '../types/api.js'
import type { ApiErrorBody } from '../types/api.js'

export const API_BASE = '/api/v1'

let getAccessToken: () => string | null = () => null

export function configureAuth(config: {
  getAccessToken: () => string | null
}) {
  getAccessToken = config.getAccessToken
}

function buildUrl(path: string): string {
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`
}

async function handleResponse(res: Response): Promise<any> {
  if (res.status === 204) return null

  const body = await res.json()

  if (!res.ok) {
    const errBody = (body as { error?: ApiErrorBody }).error ?? {
      code: 'UNKNOWN_ERROR',
      message: `Request failed with status ${res.status}`,
    }
    throw new ApiError(res.status, errBody)
  }

  return body
}

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<{ data: T }> {
  const token = getAccessToken()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(buildUrl(path), { headers, signal })
  return handleResponse(res)
}

export async function apiPost<T>(path: string, body?: unknown): Promise<{ data: T }> {
  const token = getAccessToken()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(buildUrl(path), {
    method: 'POST',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  return handleResponse(res)
}

export async function apiPostForm<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<{ data: T }> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }

  const res = await fetch(buildUrl(path), {
    method: 'POST',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  })
  return handleResponse(res)
}

export class ApiClient {
  async get<T>(path: string): Promise<T> {
    const { data } = await this.rawGet<T>(path)
    return data
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    const { data } = await apiPost<T>(path, body)
    return data
  }

  private async rawGet<T>(path: string): Promise<{ data: T }> {
    return apiGet<T>(path)
  }
}

export const apiClient = new ApiClient()